/**
 * OrderDiscounts.jsx (iter154) — Per-tenant discount codes for customer orders.
 *
 * NOT the same as /settings/promo-codes (which is superadmin-only and controls
 * Stockaris SaaS signup discounts). These codes belong to the tenant and are
 * applied to their own customers' manual orders via the Manual Order dialog.
 *
 * Features:
 *   • Percent OR fixed-amount off
 *   • Minimum order amount, start date, expiry date, max redemptions
 *   • Single-use-per-customer toggle
 *   • Restrict to specific SKUs / customers / customer price-list tiers
 *   • Live status badge (active / expired / not yet / used up / disabled)
 *   • Edit-expiry shortcut + per-row redemption audit log
 */
import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Ticket, Plus, Trash, ToggleLeft, ToggleRight, Copy, Percent, CurrencyDollar, CalendarBlank } from '@phosphor-icons/react';
import { toast } from 'sonner';
import {
  listOrderDiscounts, createOrderDiscount, updateOrderDiscount,
  deleteOrderDiscount, listOrderDiscountRedemptions,
} from '../lib/api';

function CodeEditor({ open, onClose, onSaved }) {
  const blank = {
    code: '',
    kind: 'percent',
    percent_off: 10,
    amount_off: 5,
    min_order_amount: '',
    expires_at: '',
    starts_at: '',
    max_redemptions: '',
    single_use_per_customer: false,
    restrict_sku_ids: '',
    restrict_customer_ids: '',
    restrict_price_list_tier_keys: '',
    notes: '',
  };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(blank); /* eslint-disable-next-line */ }, [open]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        code: form.code.trim().toUpperCase(),
        kind: form.kind,
        single_use_per_customer: !!form.single_use_per_customer,
        notes: form.notes || null,
        min_order_amount: form.min_order_amount === '' ? null : parseFloat(form.min_order_amount),
        max_redemptions: form.max_redemptions === '' ? null : parseInt(form.max_redemptions),
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
        restrict_sku_ids: form.restrict_sku_ids.split(',').map((s) => s.trim()).filter(Boolean),
        restrict_customer_ids: form.restrict_customer_ids.split(',').map((s) => s.trim()).filter(Boolean),
        restrict_price_list_tier_keys: form.restrict_price_list_tier_keys.split(',').map((s) => s.trim()).filter(Boolean),
      };
      if (form.kind === 'percent') body.percent_off = parseInt(form.percent_off);
      else body.amount_off = parseFloat(form.amount_off);
      await createOrderDiscount(body);
      toast.success('Code created');
      onSaved?.(); onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="od-dialog">
        <DialogHeader>
          <DialogTitle>New order discount</DialogTitle>
          <DialogDescription>This code applies only to your company&apos;s customer orders.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => upd('kind', 'percent')}
            data-testid="od-kind-percent"
            className={`p-3 border-2 text-left transition-all ${form.kind === 'percent' ? 'border-brand bg-brand/5' : 'border-border hover:border-foreground/40'}`}
          >
            <div className="flex items-center gap-2"><Percent size={16} weight="fill" className="text-brand" /><div className="font-bold text-sm">Percent off</div></div>
            <div className="text-xs text-muted-foreground mt-1">e.g. 10% off the order subtotal</div>
          </button>
          <button
            type="button"
            onClick={() => upd('kind', 'fixed')}
            data-testid="od-kind-fixed"
            className={`p-3 border-2 text-left transition-all ${form.kind === 'fixed' ? 'border-emerald-500 bg-emerald-50' : 'border-border hover:border-foreground/40'}`}
          >
            <div className="flex items-center gap-2"><CurrencyDollar size={16} weight="fill" className="text-emerald-600" /><div className="font-bold text-sm">Fixed amount</div></div>
            <div className="text-xs text-muted-foreground mt-1">e.g. $25 off if subtotal &gt; $100</div>
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-3">
          <div className="col-span-2">
            <Label>Code</Label>
            <Input data-testid="od-code" value={form.code} onChange={(e) => upd('code', e.target.value.toUpperCase())} placeholder="SUMMER10" className="font-mono" />
          </div>

          {form.kind === 'percent' ? (
            <div>
              <Label>Percent off (%)</Label>
              <Input data-testid="od-percent" type="number" min={1} max={100} value={form.percent_off} onChange={(e) => upd('percent_off', e.target.value)} />
            </div>
          ) : (
            <div>
              <Label>Amount off ($)</Label>
              <Input data-testid="od-amount" type="number" min={0.01} step={0.01} value={form.amount_off} onChange={(e) => upd('amount_off', e.target.value)} />
            </div>
          )}

          <div>
            <Label>Minimum order ($)</Label>
            <Input data-testid="od-min-order" type="number" min={0} step={0.01} value={form.min_order_amount} onChange={(e) => upd('min_order_amount', e.target.value)} placeholder="0" />
          </div>

          <div>
            <Label>Starts at <span className="text-xs text-muted-foreground">(optional)</span></Label>
            <Input data-testid="od-starts" type="datetime-local" value={form.starts_at} onChange={(e) => upd('starts_at', e.target.value)} />
          </div>
          <div>
            <Label>Expires at <span className="text-xs text-muted-foreground">(optional)</span></Label>
            <Input data-testid="od-expires" type="datetime-local" value={form.expires_at} onChange={(e) => upd('expires_at', e.target.value)} />
          </div>

          <div>
            <Label>Max redemptions <span className="text-xs text-muted-foreground">(blank = unlimited)</span></Label>
            <Input data-testid="od-max" type="number" min={1} value={form.max_redemptions} onChange={(e) => upd('max_redemptions', e.target.value)} placeholder="∞" />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="od-single-use-wrap">
              <input type="checkbox" checked={form.single_use_per_customer} onChange={(e) => upd('single_use_per_customer', e.target.checked)} data-testid="od-single-use" />
              <span className="text-sm">Single use per customer email</span>
            </label>
          </div>

          <div className="col-span-2 mt-2 pt-3 border-t border-border">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Restrictions <span className="lowercase tracking-normal text-muted-foreground/80">(optional — leave blank for no restriction)</span></Label>
          </div>
          <div className="col-span-2">
            <Label>SKU IDs (comma-separated) — code applies only if order contains at least one</Label>
            <Input data-testid="od-restrict-skus" value={form.restrict_sku_ids} onChange={(e) => upd('restrict_sku_ids', e.target.value)} placeholder="sku_id_1, sku_id_2" />
          </div>
          <div>
            <Label>Customer IDs (comma)</Label>
            <Input data-testid="od-restrict-customers" value={form.restrict_customer_ids} onChange={(e) => upd('restrict_customer_ids', e.target.value)} placeholder="cust_1, cust_2" />
          </div>
          <div>
            <Label>Price-list tier keys (comma)</Label>
            <Input data-testid="od-restrict-tiers" value={form.restrict_price_list_tier_keys} onChange={(e) => upd('restrict_price_list_tier_keys', e.target.value)} placeholder="gold, vip" />
          </div>

          <div className="col-span-2">
            <Label>Internal notes</Label>
            <Textarea data-testid="od-notes" rows={2} value={form.notes} onChange={(e) => upd('notes', e.target.value)} placeholder="Why is this code being created?" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="od-save" onClick={submit} disabled={busy}>{busy ? 'Creating…' : 'Create code'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditExpiryDialog({ codeRow, onClose, onSaved }) {
  const [val, setVal] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!codeRow) return;
    if (codeRow.expires_at) {
      const d = new Date(codeRow.expires_at);
      const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
      setVal(local);
    } else setVal('');
  }, [codeRow]);

  const save = async () => {
    setBusy(true);
    try {
      await updateOrderDiscount(codeRow.id, { expires_at: val ? new Date(val).toISOString() : null });
      toast.success(val ? 'Expiry updated' : 'Expiry cleared');
      onSaved?.(); onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!codeRow} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="od-expiry-dialog">
        <DialogHeader>
          <DialogTitle>Edit expiry for <code className="font-mono">{codeRow?.code}</code></DialogTitle>
          <DialogDescription>Pick a future date/time, or clear to remove the expiry.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Input data-testid="od-expiry-input" type="datetime-local" value={val} onChange={(e) => setVal(e.target.value)} />
          {val && new Date(val) < new Date() && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 px-2 py-1">⚠ That&apos;s in the past — the code will be rejected.</div>
          )}
          <Button variant="outline" size="sm" onClick={() => setVal('')}>Clear expiry</Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="od-expiry-save">{busy ? 'Saving…' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RedemptionsDialog({ codeRow, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!codeRow) return;
    listOrderDiscountRedemptions(codeRow.id).then((r) => setRows(r.data?.items || []));
  }, [codeRow]);
  return (
    <Dialog open={!!codeRow} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="od-redemptions">
        <DialogHeader>
          <DialogTitle>Redemptions for <code className="font-mono">{codeRow?.code}</code></DialogTitle>
          <DialogDescription>{codeRow?.redemption_count || 0} orders have used this code so far.</DialogDescription>
        </DialogHeader>
        {!rows ? (
          <div className="text-sm text-muted-foreground p-4 text-center">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-muted-foreground p-4 text-center">No redemptions yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-widest">
              <tr><th className="text-left p-2">When</th><th className="text-left p-2">Order</th><th className="text-left p-2">Customer</th><th className="text-right p-2">Discount</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border" data-testid={`od-redemption-row-${r.id}`}>
                  <td className="p-2 font-mono text-xs">{(r.redeemed_at || '').slice(0, 19).replace('T', ' ')}</td>
                  <td className="p-2 font-mono text-xs">{r.order_id?.slice(-8) || '—'}</td>
                  <td className="p-2 font-mono text-xs">{r.customer_email || '—'}</td>
                  <td className="p-2 text-xs text-right font-bold text-emerald-700">−${(r.discount_amount || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function OrderDiscounts() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [seeRedemptions, setSeeRedemptions] = useState(null);
  const [editExpiry, setEditExpiry] = useState(null);

  const load = async () => {
    try { const r = await listOrderDiscounts(); setItems(r.data?.items || []); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (row) => {
    try { await updateOrderDiscount(row.id, { active: !row.active }); load(); }
    catch (e) { toast.error('Toggle failed'); }
  };
  const doDelete = async () => {
    try { await deleteOrderDiscount(pendingDelete.id); toast.success('Deleted'); setPendingDelete(null); load(); }
    catch (e) { toast.error('Delete failed'); }
  };
  const copy = (code) => { navigator.clipboard.writeText(code); toast.success(`${code} copied`); };

  const totals = useMemo(() => ({
    active: items.filter((r) => r.active).length,
    redemptions: items.reduce((s, r) => s + (r.redemption_count || 0), 0),
  }), [items]);

  return (
    <Layout>
      <div className="p-6 max-w-6xl mx-auto" data-testid="order-discounts-page">
        <div className="flex items-center gap-3 mb-1">
          <Ticket size={28} weight="duotone" className="text-brand" />
          <h1 className="text-2xl font-bold tracking-tight">Order Discount Codes</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          Per-tenant codes for your own customer orders. Apply on the Manual Order dialog.
          (Different from Stockaris signup codes — those are platform-wide and superadmin-only.)
        </p>

        <div className="flex items-center gap-3 mb-4">
          <Badge variant="outline" data-testid="od-active-count">{totals.active} active</Badge>
          <Badge variant="outline" data-testid="od-redemption-count">{totals.redemptions} total redemptions</Badge>
          <div className="ml-auto" />
          <Button data-testid="od-new" onClick={() => setOpen(true)}><Plus size={14} className="mr-1" />New code</Button>
        </div>

        {items.length === 0 ? (
          <div className="text-sm text-muted-foreground p-8 border border-dashed border-border text-center" data-testid="od-empty">
            No codes yet. Create one above — handy for seasonal sales, VIP customers, or one-off promotions.
          </div>
        ) : (
          <div className="border border-border overflow-x-auto" data-testid="od-list">
            <table className="w-full text-sm min-w-[700px]">
              <thead className="bg-muted/40 text-xs uppercase tracking-widest">
                <tr>
                  <th className="text-left p-2">Code</th>
                  <th className="text-left p-2">Kind</th>
                  <th className="text-left p-2">Discount</th>
                  <th className="text-left p-2">Min order</th>
                  <th className="text-left p-2">Used / Limit</th>
                  <th className="text-left p-2">Expires</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-right p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const isExpired = r.expires_at && new Date(r.expires_at) < new Date();
                  const notYet = r.starts_at && new Date(r.starts_at) > new Date();
                  const isExhausted = r.max_redemptions != null && (r.redemption_count || 0) >= r.max_redemptions;
                  return (
                    <tr key={r.id} className="border-t border-border" data-testid={`od-row-${r.id}`}>
                      <td className="p-2"><code className="font-mono font-bold">{r.code}</code></td>
                      <td className="p-2">
                        {r.kind === 'percent'
                          ? <Badge className="bg-brand">Percent</Badge>
                          : <Badge className="bg-emerald-600">Fixed</Badge>}
                      </td>
                      <td className="p-2 text-xs">{r.kind === 'percent' ? `${r.percent_off}% off` : `$${Number(r.amount_off).toFixed(2)} off`}</td>
                      <td className="p-2 text-xs">{r.min_order_amount != null ? `$${Number(r.min_order_amount).toFixed(2)}` : '—'}</td>
                      <td className="p-2 tabular-nums text-xs">
                        <button onClick={() => setSeeRedemptions(r)} className="hover:underline" data-testid={`od-redemptions-${r.id}`}>
                          {r.redemption_count || 0}
                        </button>
                        <span className="text-muted-foreground"> / {r.max_redemptions ?? '∞'}</span>
                      </td>
                      <td className="p-2 text-xs">
                        {r.expires_at ? (
                          <span className={isExpired ? 'text-rose-700 font-bold' : ''}>{new Date(r.expires_at).toLocaleDateString()}</span>
                        ) : '—'}
                      </td>
                      <td className="p-2">
                        {!r.active
                          ? <Badge variant="outline" data-testid={`od-status-off-${r.id}`}>off</Badge>
                          : isExpired
                            ? <Badge className="bg-rose-600" data-testid={`od-status-expired-${r.id}`}>EXPIRED</Badge>
                            : notYet
                              ? <Badge className="bg-amber-500" data-testid={`od-status-pending-${r.id}`}>not yet</Badge>
                              : isExhausted
                                ? <Badge className="bg-zinc-600" data-testid={`od-status-exhausted-${r.id}`}>used up</Badge>
                                : <Badge className="bg-emerald-600" data-testid={`od-status-on-${r.id}`}>active</Badge>}
                      </td>
                      <td className="p-2 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" data-testid={`od-copy-${r.id}`} onClick={() => copy(r.code)} title="Copy code"><Copy size={14} /></Button>
                        <Button size="sm" variant="ghost" data-testid={`od-edit-expiry-${r.id}`} onClick={() => setEditExpiry(r)} title="Edit expiry"><CalendarBlank size={14} /></Button>
                        <Button size="sm" variant="ghost" data-testid={`od-toggle-${r.id}`} onClick={() => toggleActive(r)} title={r.active ? 'Disable' : 'Enable'}>
                          {r.active ? <ToggleRight size={16} className="text-emerald-600" weight="fill" /> : <ToggleLeft size={16} className="text-zinc-400" />}
                        </Button>
                        <Button size="sm" variant="ghost" data-testid={`od-delete-${r.id}`} onClick={() => setPendingDelete(r)}><Trash size={14} className="text-rose-600" /></Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <CodeEditor open={open} onClose={() => setOpen(false)} onSaved={load} />
        <RedemptionsDialog codeRow={seeRedemptions} onClose={() => setSeeRedemptions(null)} />
        <EditExpiryDialog codeRow={editExpiry} onClose={() => setEditExpiry(null)} onSaved={load} />

        <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
          <AlertDialogContent data-testid="od-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete code?</AlertDialogTitle>
              <AlertDialogDescription>
                <code className="font-mono font-bold">{pendingDelete?.code}</code> will be permanently removed. Past redemptions stay in the audit log.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={doDelete} data-testid="od-delete-confirm">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
